'use client'

import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { SignInControl, UserControl, useAppAuth } from '@/auth/AuthProvider'
import {
  AnalyticsIcon,
  FishingIcon,
  SettingsIcon,
  InventoryIcon,
  MissionControlIcon,
  OperationsIcon,
  CollapseIcon,
  StorefrontsIcon,
} from '@/components/icons'
import DiamondMark from '@/components/brand/DiamondMark'

const navItems = [
  { label: 'Диспетчерская', href: '/', icon: <MissionControlIcon /> },
  { label: 'Склад', href: '/inventory', icon: <InventoryIcon /> },
  { label: 'Аналитика и покупатели', href: '/analytics', icon: <AnalyticsIcon /> },
  { label: 'Рыбалка продаж', href: '/fishing', icon: <FishingIcon /> },
  { label: 'Выставка золота', href: '/gold-exhibition', icon: <StorefrontsIcon /> },
  { label: 'Операции', href: '/operations', icon: <OperationsIcon /> },
  { label: 'Настройки', href: '/settings', icon: <SettingsIcon /> },
]

export function isNavigationItemActive(href: string, pathname: string): boolean {
  if (href === '/' || href === '/settings') return pathname === href
  return pathname.startsWith(href) || (href === '/analytics' && pathname.startsWith('/customers/'))
}

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false)
  const { pathname } = useLocation()
  const { isSignedIn } = useAppAuth()
  const isFishing = pathname.startsWith('/fishing')

  return (
    <aside
      className={`hidden md:flex shrink-0 flex-col border-r overflow-hidden transition-[width] duration-200 ease-out ${
        isFishing
          ? 'bg-[linear-gradient(180deg,#1d3037_0%,#17282e_54%,#101b20_100%)] border-white/10 text-[#f7f1e5]'
          : 'bg-paper border-rule'
      } ${
        collapsed ? 'w-[60px]' : 'w-[216px]'
      }`}
    >
      {/* Brand */}
      <div
        className={`shrink-0 flex items-center border-b ${isFishing ? 'border-white/10' : 'border-rule'} ${
          collapsed ? 'h-[64px] justify-center' : 'h-[64px] px-6'
        }`}
      >
        {collapsed ? (
          <DiamondMark size={24} className={isFishing ? 'text-[#f7f1e5]' : 'text-ink'} />
        ) : (
          <div className="flex items-center gap-2.5">
            <DiamondMark size={20} className={isFishing ? 'text-[#f7f1e5]' : 'text-ink'} />
            <span className={`whitespace-nowrap text-[18px] font-semibold tracking-[0.22em] ${isFishing ? 'text-[#f7f1e5]' : 'text-ink'}`}>
              ORI
            </span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 p-2 flex flex-col gap-0.5 pt-3">
        {navItems.map((item) => {
          const isActive = isNavigationItemActive(item.href, pathname)
          return (
            <Link
              key={item.href}
              to={item.href}
              aria-current={isActive ? 'page' : undefined}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 rounded-md transition-colors duration-150 ${
                collapsed ? 'w-10 h-10 justify-center mx-auto' : 'h-10 px-3'
              } ${
                isActive
                  ? isFishing
                    ? 'bg-white/12 text-[#f7f1e5] font-semibold'
                    : 'bg-bone text-ink font-semibold'
                  : isFishing
                    ? 'text-[#f7f1e5]/70 hover:text-[#f7f1e5] hover:bg-white/8'
                    : 'text-ink-2 hover:text-ink hover:bg-bone'
              }`}
            >
              <span className="shrink-0">{item.icon}</span>
              {!collapsed && (
                <span className="whitespace-nowrap text-[13px] font-medium">
                  {item.label}
                </span>
              )}
            </Link>
          )
        })}
      </nav>

      {/* Bottom */}
      <div className={`shrink-0 border-t ${isFishing ? 'border-white/10' : 'border-rule'}`}>
        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Развернуть' : 'Свернуть'}
          className={`w-full flex items-center h-9 transition-colors duration-150 ${
            isFishing
              ? 'text-[#f7f1e5]/50 hover:text-[#f7f1e5] hover:bg-white/8'
              : 'text-ink-faint hover:text-ink hover:bg-bone'
          } ${
            collapsed ? 'justify-center' : 'px-4 gap-2'
          }`}
        >
          <CollapseIcon collapsed={collapsed} />
          {!collapsed && (
            <span className="whitespace-nowrap text-[12px]">Свернуть</span>
          )}
        </button>

        {/* User row */}
        <div
          className={`flex items-center border-t ${isFishing ? 'border-white/10' : 'border-rule/60'} ${
            collapsed ? 'justify-center py-4' : 'px-5 py-4 justify-between'
          }`}
        >
          {isSignedIn ? (
            <UserControl />
          ) : (
            <SignInControl>
              <button className={`text-[12px] transition-colors whitespace-nowrap ${isFishing ? 'text-[#f7f1e5]/60 hover:text-[#f7f1e5]' : 'text-ink-faint hover:text-ink'}`}>
                Войти
              </button>
            </SignInControl>
          )}
          {!collapsed && <VersionBadge inverted={isFishing} />}
        </div>
      </div>
    </aside>
  )
}


function VersionBadge({ inverted = false }: { inverted?: boolean }) {
  const fullSha = import.meta.env.VITE_GIT_SHA || 'dev'
  const sha = fullSha === 'dev' ? 'dev' : fullSha.slice(0, 7)
  const href =
    fullSha === 'dev'
      ? null
      : `https://github.com/rollacode/almaz-admin/commit/${fullSha}`

  const cls =
    `whitespace-nowrap text-[10px] tracking-[0.12em] uppercase tab-nums ${inverted ? 'text-[#f7f1e5]/50' : 'text-ink-faint'}`

  if (!href)
    return <span className={cls}>v.1 · {sha}</span>

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${cls} hover:text-gold-deep transition-colors`}
      title="Открыть коммит на GitHub"
    >
      v.1 · {sha}
    </a>
  )
}
